import { Capacitor } from '@capacitor/core'
import { URLS } from '@assets/environment/urls'

const NATIVE_AUTH_SCHEME = 'io.ionic.starter'
const NATIVE_AUTH_HOST = 'auth'
const PENDING_AUTH_CALLBACK_KEY = 'pending_native_auth_callback'
const PENDING_AUTH_REQUEST_KEY = 'pending_native_auth_request'
const PROGRAM_AUTH_RETURN_PATH_KEY = 'program_auth_return_path'
const FORCE_FRESH_KEYCLOAK_LOGIN_KEY = 'force_fresh_keycloak_login'
const PENDING_AUTH_REQUEST_TTL_MS = 10 * 60 * 1000
const PROGRAM_RETURN_PATH_RE = /^\/program\/\d+(?:\/(?:calendar|week(?:\/\d+)?))?$/
const OPAQUE_AUTH_PARAMETER_RE = /^[A-Za-z0-9._~-]+$/
const PKCE_CODE_VERIFIER_RE = /^[A-Za-z0-9._~-]{43,128}$/

type PendingAuthRequest = {
	state: string
	nonce: string
	codeVerifier: string
	redirectUri: string
	createdAt: number
}

const PENDING_AUTH_REQUEST_KEYS: Array<keyof PendingAuthRequest> = [
	'state',
	'nonce',
	'codeVerifier',
	'redirectUri',
	'createdAt',
]

export const isNativeAuthPlatform = (): boolean => Capacitor.isNativePlatform()

export const getAuthCallbackUrl = (path: string): string => {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`

	if (!isNativeAuthPlatform()) {
		return `${window.location.origin}${normalizedPath}`
	}

	return `${NATIVE_AUTH_SCHEME}://${NATIVE_AUTH_HOST}${normalizedPath}`
}

export const mapNativeCallbackToLocalUrl = (url: string): string | null => {
	try {
		const parsed = new URL(url)
		if (
			parsed.protocol !== `${NATIVE_AUTH_SCHEME}:` ||
			parsed.host !== NATIVE_AUTH_HOST ||
			parsed.username !== '' ||
			parsed.password !== ''
		) {
			return null
		}

		const targetPath = parsed.pathname || '/'
		return `${window.location.origin}${targetPath}${parsed.search}${parsed.hash}`
	} catch {
		return null
	}
}

export const storePendingAuthCallback = (url: string): void => {
	if (typeof window === 'undefined') return
	window.localStorage.setItem(PENDING_AUTH_CALLBACK_KEY, url)
}

export const consumePendingAuthCallback = (): string | null => {
	if (typeof window === 'undefined') return null
	const value = window.localStorage.getItem(PENDING_AUTH_CALLBACK_KEY)
	if (!value) return null
	window.localStorage.removeItem(PENDING_AUTH_CALLBACK_KEY)
	return value
}

export const clearPendingAuthCallback = (): void => {
	if (typeof window === 'undefined') return
	window.localStorage.removeItem(PENDING_AUTH_CALLBACK_KEY)
}

const toBase64Url = (buffer: ArrayBuffer): string => {
	const bytes = new Uint8Array(buffer)
	let binary = ''

	for (const byte of bytes) {
		binary += String.fromCharCode(byte)
	}

	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const randomString = (length: number): string => {
	const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
	const randomBytes = new Uint8Array(length)
	crypto.getRandomValues(randomBytes)

	return Array.from(randomBytes, value => alphabet[value % alphabet.length]).join('')
}

const createPkceChallenge = async (codeVerifier: string): Promise<string> => {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))
	return toBase64Url(digest)
}

const isValidOpaqueAuthParameter = (value: unknown): value is string =>
	typeof value === 'string' &&
	value.length >= 16 &&
	value.length <= 256 &&
	OPAQUE_AUTH_PARAMETER_RE.test(value)

const isValidPendingRedirectUri = (value: unknown): value is string => {
	if (typeof window === 'undefined' || typeof value !== 'string' || value.length > 2048) {
		return false
	}

	try {
		const parsed = new URL(value)
		if (parsed.username !== '' || parsed.password !== '') return false

		if (isNativeAuthPlatform()) {
			if (parsed.protocol !== `${NATIVE_AUTH_SCHEME}:` || parsed.host !== NATIVE_AUTH_HOST) {
				return false
			}

			const targetPath = parsed.pathname || '/'
			return (
				value ===
				`${NATIVE_AUTH_SCHEME}://${NATIVE_AUTH_HOST}${targetPath}${parsed.search}${parsed.hash}`
			)
		}

		return (
			parsed.origin === window.location.origin &&
			value === `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`
		)
	} catch {
		return false
	}
}

const isPendingAuthRequest = (value: unknown): value is PendingAuthRequest => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) return false

	const request = value as Record<string, unknown>
	const keys = Object.keys(request)
	if (
		keys.length !== PENDING_AUTH_REQUEST_KEYS.length ||
		!PENDING_AUTH_REQUEST_KEYS.every(key => Object.prototype.hasOwnProperty.call(request, key))
	) {
		return false
	}

	return (
		isValidOpaqueAuthParameter(request.state) &&
		isValidOpaqueAuthParameter(request.nonce) &&
		typeof request.codeVerifier === 'string' &&
		PKCE_CODE_VERIFIER_RE.test(request.codeVerifier) &&
		isValidPendingRedirectUri(request.redirectUri) &&
		typeof request.createdAt === 'number' &&
		Number.isSafeInteger(request.createdAt) &&
		request.createdAt > 0
	)
}

const isFreshPendingAuthRequest = (request: PendingAuthRequest): boolean => {
	const age = Date.now() - request.createdAt
	return age >= 0 && age <= PENDING_AUTH_REQUEST_TTL_MS
}

export const createKeycloakLoginUrl = async (path: string): Promise<string> => {
	clearPendingAuthRequest()

	const redirectUri = getAuthCallbackUrl(path)
	const state = crypto.randomUUID()
	const nonce = crypto.randomUUID()
	const codeVerifier = randomString(96)
	const codeChallenge = await createPkceChallenge(codeVerifier)

	const request: PendingAuthRequest = {
		state,
		nonce,
		codeVerifier,
		redirectUri,
		createdAt: Date.now(),
	}

	window.localStorage.setItem(PENDING_AUTH_REQUEST_KEY, JSON.stringify(request))

	const params = new URLSearchParams({
		client_id: URLS.KC_USER,
		redirect_uri: redirectUri,
		state,
		response_mode: 'fragment',
		response_type: 'code',
		scope: 'openid',
		nonce,
		code_challenge: codeChallenge,
		code_challenge_method: 'S256',
	})

	if (consumeFreshKeycloakLoginRequest()) {
		params.set('prompt', 'login')
		params.set('max_age', '0')
	}

	return `${URLS.KC_URL}realms/${URLS.KC_REALM}/protocol/openid-connect/auth?${params.toString()}`
}

export const getPendingAuthRequest = (expectedState?: string): PendingAuthRequest | null => {
	if (typeof window === 'undefined') return null
	const rawValue = window.localStorage.getItem(PENDING_AUTH_REQUEST_KEY)
	if (!rawValue) return null

	try {
		const request: unknown = JSON.parse(rawValue)
		if (!isPendingAuthRequest(request) || !isFreshPendingAuthRequest(request)) {
			window.localStorage.removeItem(PENDING_AUTH_REQUEST_KEY)
			return null
		}
		if (expectedState !== undefined && request.state !== expectedState) {
			return null
		}

		window.localStorage.removeItem(PENDING_AUTH_REQUEST_KEY)
		return request
	} catch {
		window.localStorage.removeItem(PENDING_AUTH_REQUEST_KEY)
		return null
	}
}

export const clearPendingAuthRequest = (): void => {
	if (typeof window === 'undefined') return
	window.localStorage.removeItem(PENDING_AUTH_REQUEST_KEY)
}

export const clearPendingAuthState = (): void => {
	clearPendingAuthCallback()
	clearPendingAuthRequest()
}

export const requestFreshKeycloakLogin = (): void => {
	if (typeof window === 'undefined') return
	window.localStorage.setItem(FORCE_FRESH_KEYCLOAK_LOGIN_KEY, '1')
}

const consumeFreshKeycloakLoginRequest = (): boolean => {
	if (typeof window === 'undefined') return false
	const value = window.localStorage.getItem(FORCE_FRESH_KEYCLOAK_LOGIN_KEY)
	window.localStorage.removeItem(FORCE_FRESH_KEYCLOAK_LOGIN_KEY)
	return value === '1'
}

const normalizeProgramReturnPath = (path: string): string | null => {
	if (typeof window === 'undefined') return null

	try {
		const parsedUrl = new URL(path, window.location.origin)
		if (parsedUrl.origin !== window.location.origin) return null
		if (!PROGRAM_RETURN_PATH_RE.test(parsedUrl.pathname)) return null

		return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
	} catch {
		return null
	}
}

export const storeProgramAuthReturnPath = (path: string): void => {
	if (typeof window === 'undefined') return

	const normalizedPath = normalizeProgramReturnPath(path)
	if (!normalizedPath) return

	window.localStorage.setItem(PROGRAM_AUTH_RETURN_PATH_KEY, normalizedPath)
}

export const consumeProgramAuthReturnPath = (): string | null => {
	if (typeof window === 'undefined') return null

	const storedPath = window.localStorage.getItem(PROGRAM_AUTH_RETURN_PATH_KEY)
	window.localStorage.removeItem(PROGRAM_AUTH_RETURN_PATH_KEY)
	if (!storedPath) return null

	return normalizeProgramReturnPath(storedPath)
}
